import { useState, useContext, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { OrganizationContext } from "../../clients/utils/OrganizationContext";

const API = "https://api.komorotel.com/api";

const MODES = [
  {
    value: "mission",
    titre: "Par mission",
    texte: "Le membre réserve librement dans l'enveloppe de sa mission. Toute réservation en dehors attend votre accord.",
    conseille: true,
  },
  {
    value: "always_approve",
    titre: "Validation systématique",
    texte: "Le membre peut réserver, mais chaque réservation attend votre accord. Utile si vous ne voulez pas gérer de missions.",
  },
  {
    value: "centralized",
    titre: "Centralisé",
    texte: "Les membres ne réservent pas. Vous passez les réservations pour eux.",
  },
];

export default function OrgSettings() {
  const { organization, refetch } = useContext(OrganizationContext);
  const [mode, setMode]       = useState("mission");
  const [infos, setInfos]     = useState({ name: "", address: "", phone: "", email: "" });
  const [billing, setBilling] = useState({ billingEmail: "", address: "", taxId: "" });
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (!organization) return;
    setMode(organization.memberBookingMode || "mission");
    setInfos({
      name:    organization.name    || "",
      address: organization.address || "",
      phone:   organization.phone   || "",
      email:   organization.email   || "",
    });
    setBilling({
      billingEmail: organization.billing?.billingEmail || "",
      address:      organization.billing?.address      || "",
      taxId:        organization.billing?.taxId        || "",
    });
  }, [organization]);

  const titulaire = organization?.managers
    ?.find(m => m._id === organization?.currentManagerId)?.role === "owner";

  const enregistrer = async (payload, message) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/organization/settings`, payload, { withCredentials: true });
      await refetch();
      toast.success(message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur.");
    } finally {
      setSaving(false);
    }
  };

  const champ = (label, value, onChange, type = "text") => (
    <label key={label} className="block">
      <span className="text-[13px] text-[var(--org-slate)]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full mt-1 py-2 text-[15px] bg-transparent outline-none text-[var(--org-ink)] transition"
        style={{ borderBottom: "1px solid var(--org-line)" }}
      />
    </label>
  );

  const bouton = (onClick, libelle) => (
    <button
      onClick={onClick}
      disabled={saving}
      className="mt-5 px-3.5 py-[7px] rounded-lg text-[13.5px] font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
      style={{ background: "var(--org-forest)" }}
    >
      {saving ? "Enregistrement..." : libelle}
    </button>
  );

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-8 sm:py-10 max-w-[720px]">

      <header className="mb-9">
        <h1 className="text-[23px] sm:text-[27px] leading-tight font-semibold tracking-[-0.01em] text-[var(--org-ink)]">
          Réglages
        </h1>
        <p className="text-[13.5px] sm:text-[13.5px] text-[var(--org-slate)] mt-1">
          Comment vos membres réservent, et vos coordonnées de facturation
        </p>
      </header>

      {/* ── Mode de réservation ── */}
      <section className="pb-9" style={{ borderBottom: "1px solid var(--org-line)" }}>
        <h2 className="text-[17px] font-semibold text-[var(--org-ink)]">Réservation par les membres</h2>
        <p className="text-[13.5px] text-[var(--org-slate)] mt-1 mb-5 max-w-lg">
          Ce réglage décide de ce qu'un membre peut engager sans vous demander.
        </p>

        <div className="flex flex-col gap-2.5">
          {MODES.map(m => {
            const choisi = mode === m.value;
            return (
              <label
                key={m.value}
                className={`flex gap-3.5 p-4 rounded-lg cursor-pointer transition
                  ${!titulaire ? "opacity-55 cursor-not-allowed" : ""}`}
                style={{
                  border: `1px solid ${choisi ? "var(--org-forest)" : "var(--org-line)"}`,
                  background: choisi ? "#fff" : "transparent",
                }}
              >
                <input
                  type="radio"
                  name="mode"
                  value={m.value}
                  checked={choisi}
                  disabled={!titulaire}
                  onChange={() => setMode(m.value)}
                  className="mt-1 accent-[var(--org-forest)]"
                />
                <div>
                  <p className="text-[14px] text-[var(--org-ink)]">
                    {m.titre}
                    {m.conseille && (
                      <span className="ml-2 text-[12.5px] text-[var(--org-slate)]">conseillé</span>
                    )}
                  </p>
                  <p className="text-[13.5px] text-[var(--org-slate)] mt-1">{m.texte}</p>
                </div>
              </label>
            );
          })}
        </div>

        {!titulaire && (
          <p className="text-[13.5px] text-[var(--org-slate)] mt-4">
            Seul le titulaire du compte peut changer ce réglage.
          </p>
        )}

        {titulaire && mode !== organization?.memberBookingMode &&
          bouton(() => enregistrer({ memberBookingMode: mode }, "Mode de réservation enregistré."), "Enregistrer le mode")}
      </section>

      {/* ── Coordonnées ── */}
      <section className="py-9" style={{ borderBottom: "1px solid var(--org-line)" }}>
        <h2 className="text-[17px] font-semibold text-[var(--org-ink)] mb-5">Coordonnées</h2>
        <div className="flex flex-col gap-5 max-w-md">
          {champ("Nom de l'organisation", infos.name,    v => setInfos(s => ({ ...s, name: v })))}
          {champ("Adresse",               infos.address, v => setInfos(s => ({ ...s, address: v })))}
          {champ("Téléphone",             infos.phone,   v => setInfos(s => ({ ...s, phone: v })), "tel")}
          {champ("Email général",         infos.email,   v => setInfos(s => ({ ...s, email: v })), "email")}
        </div>
        {bouton(() => enregistrer(infos, "Coordonnées enregistrées."), "Enregistrer")}
      </section>

      {/* ── Facturation ── */}
      <section className="pt-9">
        <h2 className="text-[17px] font-semibold text-[var(--org-ink)]">Facturation</h2>
        <p className="text-[13.5px] text-[var(--org-slate)] mt-1 mb-5 max-w-lg">
          Un relevé est émis chaque mois et regroupe tous les séjours de la période.
        </p>
        <div className="flex flex-col gap-5 max-w-md">
          {champ("Email de facturation",   billing.billingEmail, v => setBilling(s => ({ ...s, billingEmail: v })), "email")}
          {champ("Adresse de facturation", billing.address,      v => setBilling(s => ({ ...s, address: v })))}
          {champ("Numéro fiscal",          billing.taxId,        v => setBilling(s => ({ ...s, taxId: v })))}
        </div>
        {bouton(() => enregistrer({ billing }, "Facturation enregistrée."), "Enregistrer")}
      </section>
    </div>
  );
}
